import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { debounce } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridToolbarContainer } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { Delete, Download, Search } from '@mui/icons-material';

import { timeZoneAtom } from '@/interfaces/utils';
import { reportsAtom } from '@/interfaces/site';
import { deleteReportAtom, getReportUrl } from '@/interfaces/report';
import { compareInLowerCase, download } from '@/components/common/utils';
import { SettingLayout } from '@/components/setting/SettingLayout';

import type { ChangeEvent } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import type { GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import type { ReportListData } from '@/@types/site';

export default function Reports() {
  const timeZone = useAtomValue(timeZoneAtom);
  const { data: reports, refetch } = useAtomValue(reportsAtom);
  const { mutate: deleteReport, data: isDeleteSuccess } = useAtomValue(deleteReportAtom);

  const [formatQuery, setFormatQuery] = useState('all');
  const [textQuery, setTextQuery] = useState('');
  const [rows, setRows] = useState<ReportListData[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const { t, i18n } = useTranslation();

  const isKorean = i18n.language.startsWith('ko');
  const locale = isKorean ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const timeFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';

  useEffect(() => {
    if (!reports) return;
    setRows(reports);
  }, [reports]);
  useEffect(() => {
    if (isDeleteSuccess === undefined) return;
    if (isDeleteSuccess) {
      refetch();
    } else {
      window.alert(t('Devices.message.reportDeleteFail'));
    }
  }, [isDeleteSuccess, refetch, t]);

  const getActions = useCallback(
    (params: GridRowParams<ReportListData>) => {
      const { id: reportId, name } = params.row;

      async function onDownloadClick(reportId: string) {
        const url = await getReportUrl(reportId);
        download(url);
      }
      function onDeleteClick(name: string, reportId: string) {
        const ok = window.confirm(`${t('Common.message.confirmDelete')}\n${t('Common.label.name')}: ${name}`);
        if (ok) {
          deleteReport(reportId);
        }
      }
      return [
        <GridActionsCellItem
          icon={<Download fontSize="large" color="success" />}
          label="download"
          onClick={() => onDownloadClick(reportId)}
        />,
        <GridActionsCellItem
          icon={<Delete fontSize="large" color="error" />}
          label="delete"
          onClick={() => onDeleteClick(name, reportId)}
        />,
      ];
    },
    [deleteReport, t],
  );
  const columns = useMemo<GridColDef<ReportListData>[]>(
    () => [
      { field: 'name', headerName: t('Common.label.name'), flex: 4, sortable: false },
      { field: 'serialNumber', headerName: t('Devices.label.serialNumber'), flex: 2, sortable: false },
      { field: 'format', headerName: t('Devices.label.fileFormat'), flex: 1, sortable: false },
      {
        field: 'createdAt',
        type: 'dateTime',
        headerName: t('Common.label.createdOn'),
        flex: 2,
        sortable: true,
        valueGetter: (_, row) => row.timeInfo.createdAt,
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
      { field: 'status', headerName: t('Common.label.status'), flex: 1, sortable: false },
      {
        field: 'action',
        type: 'actions',
        headerName: t('Common.label.action'),
        flex: 1,
        sortable: false,
        getActions,
      },
    ],
    [getActions, t, timeFormat, timeZone],
  );
  function Toolbar() {
    return (
      <GridToolbarContainer>
        <Button variant="contained" disabled={selectedRows.length === 0} onClick={onBatchDownloadClick} sx={{ m: 1 }}>
          {t('Common.label.batchDownload')}
        </Button>
      </GridToolbarContainer>
    );
  }

  const filterReports = useMemo(
    () =>
      debounce((formatFilter: string, queryFilter: string) => {
        setRows(
          reports.filter(
            (row) =>
              (compareInLowerCase(row.serialNumber, queryFilter) || compareInLowerCase(row.name, queryFilter)) &&
              (formatFilter === 'all' ? true : row.format === formatFilter),
          ),
        );
      }, 500),
    [reports],
  );
  function onSelectChange(e: SelectChangeEvent) {
    const { value } = e.target;
    setFormatQuery(value);
    filterReports(value, textQuery);
  }
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setTextQuery(value);
    filterReports(formatQuery, value);
  }
  async function onBatchDownloadClick() {
    for (const reportId of selectedRows) {
      const url = await getReportUrl(reportId as string);
      download(url);
    }
  }

  return (
    <SettingLayout>
      <Stack gap={3} sx={{ height: '100%' }}>
        <Typography variant="h5" fontWeight="bold">
          {t('Common.label.reports')}
        </Typography>

        <Stack direction="row" gap={2}>
          <FormControl>
            <InputLabel>{t('Devices.label.fileFormat')}</InputLabel>
            <Select
              label={t('Devices.label.fileFormat')}
              value={formatQuery}
              onChange={onSelectChange}
              sx={{ width: 160, bgcolor: 'white' }}
            >
              <MenuItem value="all">{t('Common.label.all')}</MenuItem>
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="CSV">CSV</MenuItem>
              <MenuItem value="FDA">FDA</MenuItem>
            </Select>
          </FormControl>
          <TextField
            placeholder={t('Common.label.search')}
            value={textQuery}
            onChange={onQueryChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '300px', bgcolor: 'white' }}
          />
        </Stack>

        <DataGrid
          checkboxSelection
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(e) => setSelectedRows(e)}
          slots={{ toolbar: Toolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>
    </SettingLayout>
  );
}
